<template>
  <div class="app-container">
    <el-card shadow="never" class="query-card beauty-card">
      <div class="table-toolbar">
        <div class="left page-title">图片生成任务</div>
        <div class="right">
          <el-button size="mini" icon="el-icon-refresh" @click="fetchList">刷新</el-button>
        </div>
      </div>

      <el-table
        v-loading="loading"
        :data="taskList"
        class="beauty-table"
        :header-cell-style="{ background: '#f8fafd', color: '#606266', fontWeight: '600' }"
      >
        <el-table-column prop="id" label="任务ID" width="80" align="center" />
        <el-table-column prop="mode" label="类型" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag size="small" type="warning">{{ row.mode === 'derive' ? '批量衍生' : row.mode }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="model" label="模型" min-width="140" show-overflow-tooltip />
        <el-table-column prop="prompt" label="提示词" min-width="180" show-overflow-tooltip />
        <el-table-column label="进度" width="180" align="center">
          <template slot-scope="{ row }">
            <div>
              <el-tag :type="statusType(row.status)" size="small">{{ statusText(row.status) }}</el-tag>
              <div class="progress-text">
                成功 {{ row.successCount || 0 }} / 失败 {{ row.failCount || 0 }} / 共 {{ row.totalCount || 0 }}
              </div>
              <el-progress
                v-if="isRunning(row.status) && row.totalCount > 0"
                :percentage="calcPercent(row)"
                :stroke-width="6"
                :show-text="false"
                style="margin-top: 6px;"
              />
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="userNickname" label="提交人" width="100" align="center" />
        <el-table-column prop="createdAt" label="创建时间" width="170" align="center" />
        <el-table-column prop="completedAt" label="完成时间" width="170" align="center" />
        <el-table-column label="操作" align="center" fixed="right" width="100">
          <template slot-scope="{ row }">
            <el-button size="mini" type="text" icon="el-icon-view" @click="openDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          :current-page="query.pageNo"
          :page-sizes="[10, 20, 50]"
          :page-size="query.pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <el-dialog title="任务详情" :visible.sync="detailVisible" width="1100px" append-to-body custom-class="beauty-dialog">
      <div v-if="detailBatch" class="detail-meta">
        <div>模型：{{ detailBatch.model }}</div>
        <div>尺寸：{{ detailBatch.resolution }} / {{ detailBatch.aspectRatio }}</div>
        <div>命名：{{ detailBatch.namingMode === 'derive' ? '衍生命名' : '顺序编号' }}
          <span v-if="detailBatch.deriveNameRule">（{{ detailBatch.deriveNameRule }}）</span>
        </div>
        <div class="prompt-block">提示词：{{ detailBatch.prompt }}</div>
      </div>
      <el-table :data="detailItems" size="small" max-height="480" v-loading="detailLoading">
        <el-table-column prop="id" label="明细ID" width="70" align="center" />
        <el-table-column label="原图" width="88" align="center">
          <template slot-scope="{ row }">
            <img
              v-if="row.sourceImageUrl"
              :src="row.sourceImageUrl"
              class="detail-thumb"
              loading="lazy"
              title="点击预览原图"
              @click="openPreview(row, 'source')"
            />
            <span v-else class="thumb-empty">-</span>
          </template>
        </el-table-column>
        <el-table-column label="生成图" width="88" align="center">
          <template slot-scope="{ row }">
            <img
              v-if="row.resultImageUrl"
              :src="row.resultImageUrl"
              class="detail-thumb"
              loading="lazy"
              title="点击预览生成图"
              @click="openPreview(row, 'result')"
            />
            <span v-else class="thumb-empty">{{ row.status === 'success' ? '无图' : '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="sourceMaterialName" label="源素材" min-width="120" show-overflow-tooltip />
        <el-table-column prop="targetName" label="目标名称" min-width="120" show-overflow-tooltip />
        <el-table-column prop="status" label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="itemStatusType(row.status)" size="mini">{{ itemStatusText(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="resultMaterialId" label="新素材ID" width="90" align="center" />
        <el-table-column prop="errorMsg" label="失败原因" min-width="120" show-overflow-tooltip />
      </el-table>
      <div slot="footer">
        <el-button @click="detailVisible = false">关闭</el-button>
        <el-button type="primary" icon="el-icon-refresh" :loading="detailLoading" @click="reloadDetail">刷新</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :title="previewTitle"
      :visible.sync="previewVisible"
      append-to-body
      width="auto"
      top="5vh"
      custom-class="ai-preview-dialog"
      @closed="resetPreview"
    >
      <div class="preview-compare" v-if="previewRow">
        <div class="preview-pane" v-if="previewRow.sourceImageUrl">
          <div class="preview-label">原图</div>
          <img :src="previewRow.sourceImageUrl" class="preview-img" alt="原图" />
        </div>
        <div class="preview-pane" v-if="previewRow.resultImageUrl">
          <div class="preview-label">生成图</div>
          <img :src="previewRow.resultImageUrl" class="preview-img" alt="生成图" />
        </div>
        <div v-if="!previewRow.sourceImageUrl && !previewRow.resultImageUrl" class="thumb-empty">暂无可预览图片</div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listAiGenerateBatches, getAiGenerateBatch } from '@/api/material/aiGenerate';

export default {
  name: 'AiGenerateTasks',
  data() {
    return {
      loading: false,
      taskList: [],
      total: 0,
      query: { pageNo: 1, pageSize: 10 },
      pollTimer: null,
      detailVisible: false,
      detailLoading: false,
      detailBatch: null,
      detailItems: [],
      detailId: null,
      previewVisible: false,
      previewRow: null,
      previewFocus: 'source'
    };
  },
  computed: {
    previewTitle() {
      if (!this.previewRow) return '图片预览';
      const name = this.previewRow.targetName || this.previewRow.sourceMaterialName || '';
      return name ? `图片预览 - ${name}` : '图片预览';
    }
  },
  mounted() {
    this.fetchList();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async fetchList() {
      this.loading = true;
      try {
        const res = await listAiGenerateBatches({
          pageNo: this.query.pageNo,
          pageSize: this.query.pageSize
        });
        const page = res.data || res;
        this.taskList = page.records || [];
        this.total = page.total || 0;
        const hasRunning = this.taskList.some(t => this.isRunning(t.status));
        if (hasRunning) this.startPolling();
        else this.stopPolling();
      } catch (e) {
        this.$message.error('加载失败：' + (e && e.message ? e.message : '未知错误'));
      } finally {
        this.loading = false;
      }
    },
    startPolling() {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => this.fetchList(), 4000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    handleSizeChange(val) {
      this.query.pageSize = val;
      this.query.pageNo = 1;
      this.fetchList();
    },
    handleCurrentChange(val) {
      this.query.pageNo = val;
      this.fetchList();
    },
    isRunning(status) {
      return status === 'pending' || status === 'processing';
    },
    statusText(status) {
      const map = {
        pending: '等待中',
        processing: '生成中',
        completed: '已完成',
        partial_failed: '部分失败',
        failed: '失败'
      };
      return map[status] || status;
    },
    statusType(status) {
      const map = {
        pending: 'info',
        processing: 'warning',
        completed: 'success',
        partial_failed: 'warning',
        failed: 'danger'
      };
      return map[status] || 'info';
    },
    itemStatusText(status) {
      const map = {
        pending: '等待',
        submitting: '提交中',
        generating: '生成中',
        success: '成功',
        failed: '失败'
      };
      return map[status] || status;
    },
    itemStatusType(status) {
      const map = {
        pending: 'info',
        submitting: 'warning',
        generating: 'warning',
        success: 'success',
        failed: 'danger'
      };
      return map[status] || 'info';
    },
    calcPercent(row) {
      const total = Number(row.totalCount) || 0;
      const done = (Number(row.successCount) || 0) + (Number(row.failCount) || 0);
      if (total <= 0) return 0;
      return Math.max(0, Math.min(100, Math.round((done / total) * 100)));
    },
    async openDetail(row) {
      this.detailId = row.id;
      this.detailVisible = true;
      await this.reloadDetail();
    },
    async reloadDetail() {
      if (!this.detailId) return;
      this.detailLoading = true;
      try {
        const res = await getAiGenerateBatch(this.detailId);
        const data = res.data || {};
        this.detailBatch = data.batch || null;
        this.detailItems = data.items || [];
      } catch (e) {
        this.$message.error('加载详情失败');
      } finally {
        this.detailLoading = false;
      }
    },
    openPreview(row, focus) {
      if (!row) return;
      if (!row.sourceImageUrl && !row.resultImageUrl) {
        this.$message.info('暂无可预览图片');
        return;
      }
      this.previewRow = row;
      this.previewFocus = focus || 'source';
      this.previewVisible = true;
    },
    resetPreview() {
      this.previewRow = null;
      this.previewFocus = 'source';
    }
  }
};
</script>

<style scoped>
.page-title {
  font-weight: 600;
  color: #303133;
}
.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.pagination-container {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
.progress-text {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}
.detail-meta {
  margin-bottom: 12px;
  font-size: 13px;
  color: #606266;
  line-height: 1.7;
}
.prompt-block {
  margin-top: 6px;
  padding: 8px 10px;
  background: #f5f7fa;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-word;
}
.detail-thumb {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #ebeef5;
  background: #f5f7fa;
  vertical-align: middle;
}
.detail-thumb:hover {
  border-color: #409eff;
}
.thumb-empty {
  color: #c0c4cc;
  font-size: 12px;
}
.preview-compare {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: center;
  flex-wrap: wrap;
  max-width: 92vw;
}
.preview-pane {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 200px;
}
.preview-label {
  margin-bottom: 8px;
  font-size: 13px;
  color: #606266;
  font-weight: 600;
}
.preview-img {
  max-width: min(44vw, 520px);
  max-height: 72vh;
  object-fit: contain;
  border-radius: 4px;
  background: #f5f7fa;
}
</style>
